import React from 'react';
import {Typography, Tooltip, Flex, Switch} from 'antd';
import {InfoCircleOutlined} from '@ant-design/icons';
import {ModelDTO, UserDTO} from 'hemwb-api';
import {ModelEditPermissionsFormData} from '../../model/common/ModelEdit/ModelEditPermissons/ModelEditPermissionsForm';
import {ModelTreeNode} from '../../../../store/modelFile';
import {isModelWeb, useModels} from '../../../../store/model';
import {fields} from '../../model/common/ModelEdit/ModelEditMetadata/constants';
import UserCardAntd from '../../../core/UserCard/UserCardAntd';

const {Title, Text, Paragraph} = Typography;

interface StepReviewRedesignedProps {
  model: ModelDTO;
  revopsName?: string;
  permissions: ModelEditPermissionsFormData;
  files?: ModelTreeNode[];
  onSubmit: () => void;
  onBack?: () => void;
  /** HEMT-8938: current opt-out preference chosen by the user during creation */
  aiSummaryOptedOut?: boolean;
  /** HEMT-8938: callback when user changes opt-out preference */
  onAiSummaryOptedOutChange?: (optedOut: boolean) => void;
  buttons?: (params: {onBack?: () => void; nextHtmlType?: 'button' | 'submit' | 'reset'}) => React.ReactNode;
}

const StepReviewRedesigned: React.FC<StepReviewRedesignedProps> = ({
  model,
  revopsName,
  permissions,
  files,
  onBack,
  aiSummaryOptedOut = false,
  onAiSummaryOptedOutChange,
  buttons,
}) => {
  const models = useModels();
  const webModel = isModelWeb(model);

  const selectedParentModel = model.metadata?.tree_parent_id
    ? ((models && (models as Record<string, ModelDTO>)[model.metadata.tree_parent_id]) as ModelDTO | undefined)
    : undefined;

  const linkMyModelEntryValue =
    selectedParentModel?.metadata?.therapeutic_area ||
    selectedParentModel?.name ||
    model.metadata?.tree_parent_id ||
    '-';

  const getModelSteward = (modelData: ModelDTO): UserDTO | undefined => {
    if (
      modelData.metadata?.isid_of_model_steward &&
      modelData.metadata?.name_of_model_steward &&
      modelData.metadata?.email_for_model_steward
    ) {
      const [firstName, ...lastNameParts] = modelData.metadata.name_of_model_steward.split(' ');
      return {
        id: modelData.metadata.isid_of_model_steward,
        firstName,
        lastName: lastNameParts.join(' '),
        email: modelData.metadata.email_for_model_steward,
      } as UserDTO;
    }
    return undefined;
  };

  const renderLinksValue = (value: string | undefined) => {
    if (!value) return <Text style={{fontSize: '14px', color: '#262626', fontWeight: 500}}>-</Text>;

    const parseSegments = (line: string) => {
      const segments: Array<{type: 'text' | 'url'; value: string}> = [];
      const urlRegex = /https?:\/\/[^\s]+/g;
      let lastIndex = 0;
      let match;
      while ((match = urlRegex.exec(line)) !== null) {
        if (match.index > lastIndex) segments.push({type: 'text', value: line.slice(lastIndex, match.index)});
        segments.push({type: 'url', value: match[0]});
        lastIndex = match.index + match[0].length;
      }
      if (lastIndex < line.length) segments.push({type: 'text', value: line.slice(lastIndex)});
      return segments;
    };

    const lines = value.split('\n').filter((line) => line.trim() !== '');
    return (
      <div style={{display: 'flex', flexDirection: 'column', gap: '4px'}}>
        {lines.map((line, idx) => {
          const segments = parseSegments(line.trim());
          return (
            <span key={idx} style={{wordBreak: 'break-all'}}>
              {segments.map((seg, segIdx) =>
                seg.type === 'url' ? (
                  <a
                    key={segIdx}
                    href={seg.value}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{fontSize: '14px', color: '#006A63'}}
                  >
                    {seg.value}
                  </a>
                ) : (
                  <Text key={segIdx} style={{fontSize: '14px', color: '#262626', fontWeight: 500}}>
                    {seg.value}
                  </Text>
                ),
              )}
            </span>
          );
        })}
      </div>
    );
  };

  const renderInfoRow = (label: string, value: string | undefined, required?: boolean, helperText?: string) => {
    return (
      <div style={{marginBottom: '16px'}}>
        <Flex align="center" gap={4} style={{marginBottom: '4px'}}>
          <Text style={{fontSize: '14px', color: '#666'}}>
            {label} {required && <span style={{color: '#ff4d4f'}}>*</span>}
          </Text>
          {helperText && <Text style={{fontSize: '12px', color: '#8c8c8c'}}>{helperText}</Text>}
        </Flex>
        <Text style={{fontSize: '14px', color: '#262626', fontWeight: 500}}>{value || '-'}</Text>
      </div>
    );
  };

  const renderUsersOrDash = (users?: UserDTO[]) => {
    if (users && users.length > 0) {
      return (
        <div style={{display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: '10px'}}>
          {users.map((user) => (
            <UserCardAntd key={user.id} user={user} showStatusTags style={{marginBottom: 0}} />
          ))}
        </div>
      );
    }
    return <Text style={{fontSize: '14px', color: '#8c8c8c'}}>-</Text>;
  };

  const renderPermissionCell = (label: string, content: React.ReactNode, required?: boolean) => (
    <div style={{marginBottom: '16px'}}>
      <Text style={{fontSize: '14px', color: '#666', display: 'block', marginBottom: '8px'}}>
        {label} {required && <span style={{color: '#ff4d4f'}}>*</span>}
      </Text>
      {content}
    </div>
  );

  const modelSteward = getModelSteward(model);

  const renderSection = (title: string, children: React.ReactNode) => (
    <div
      style={{
        background: '#fafafa',
        border: '2px dashed #d9d9d9',
        borderRadius: '8px',
        padding: '24px',
        marginBottom: '24px',
      }}
    >
      <Title level={4} style={{fontSize: '16px', fontWeight: 500, marginBottom: '16px', color: '#006A63'}}>
        {title}
      </Title>
      {children}
    </div>
  );

  return (
    <div style={{background: '#ffffff', minHeight: '100vh', padding: '40px 80px', paddingBottom: '120px'}}>
      <div style={{maxWidth: '1200px', margin: '0 auto'}}>
        {/* Metadata Section */}
        <div style={{marginBottom: '40px'}}>
          <Title level={2} style={{fontSize: '20px', fontWeight: 500, marginBottom: '16px', color: '#262626'}}>
            Metadata
          </Title>

          {/* Model Identification */}
          {renderSection(
            'Model Identification',
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px'}}>
              <div>
                {renderInfoRow(
                  fields.revops_id,
                  revopsName || model.metadata?.revops_name || model.metadata?.revops_id,
                  undefined,
                  '(Select to auto-populate related metadata fields)',
                )}
                {renderInfoRow(fields.study_number, model.metadata?.study_number)}
                {renderInfoRow(fields.type, model.metadata?.type, true)}
                {renderInfoRow(fields.information_classification, model.metadata?.information_classification, true)}
              </div>
              <div>
                {renderInfoRow(fields.product, model.metadata?.product, true)}
                {renderInfoRow(fields.disease, model.metadata?.disease, true)}
                {renderInfoRow(fields.therapeutic_area, model.metadata?.therapeutic_area, true)}
                {renderInfoRow(fields.model_type, model.metadata?.model_type, true)}
                {renderInfoRow('Enable WebModel 2.0 ⓘ', model.metadata?.platform === 'WebModel 2.0' ? 'Yes' : 'No')}
              </div>
            </div>,
          )}

          {/* Country Association */}
          {renderSection(
            'Country Association',
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 24px'}}>
              <div>{renderInfoRow(fields.country, model.metadata?.country, model.metadata?.global !== 'true')}</div>
              <div>{renderInfoRow('Is this a Global Model?', model.metadata?.global === 'true' ? 'Yes' : 'No')}</div>
            </div>,
          )}

          {/* Model Suffix */}
          {renderSection(
            'Model Suffix',
            <>
              <Paragraph style={{fontSize: '13px', color: '#595959', marginBottom: '12px'}}>
                Provide additional context or identifier for this model entry
              </Paragraph>
              <Text style={{fontSize: '14px', color: '#262626'}}>{model.metadata?.suffix || '-'}</Text>
            </>,
          )}

          {/* Documentation */}
          {renderSection(
            'Documentation',
            <>
              <Text style={{fontSize: '14px', color: '#666', display: 'block', marginBottom: '8px'}}>
                {fields.objective_of_model}
              </Text>
              <Text style={{fontSize: '14px', color: '#262626'}}>{model.metadata?.objective_of_model || '-'}</Text>
            </>,
          )}

          {/* Notes */}
          {renderSection(
            'Notes',
            <>
              <div style={{marginTop: '16px'}}>{renderInfoRow(fields.notes, model.metadata?.notes)}</div>
              <div style={{marginTop: '16px'}}>
                <div style={{marginBottom: '16px'}}>
                  <Text style={{fontSize: '14px', color: '#666', display: 'block', marginBottom: '4px'}}>
                    {fields.training_and_publication_link}
                  </Text>
                  {renderLinksValue(model.metadata?.training_and_publication_link)}
                </div>
              </div>
            </>,
          )}

          {/* Version Number */}
          {renderSection(
            'Version Number',
            <>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px'}}>
                <div>
                  <div style={{marginBottom: '16px'}}>
                    <Text style={{fontSize: '14px', color: '#666', display: 'block', marginBottom: '4px'}}>
                      Version Number <span style={{color: '#ff4d4f'}}>*</span>
                    </Text>
                    <Text style={{fontSize: '14px', color: '#262626', fontWeight: 500}}>
                      {model.metadata?.version_release_of_model || '-'}
                    </Text>
                  </div>
                  <div>
                    <Text style={{fontSize: '14px', color: '#666', display: 'block', marginBottom: '4px'}}>
                      {fields.version_log}
                    </Text>
                    <Text style={{fontSize: '14px', color: '#262626'}}>{model.metadata?.version_log || '-'}</Text>
                  </div>
                </div>
                <div>
                  <div style={{display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px'}}>
                    <Text style={{fontSize: '14px', color: '#666'}}>{fields.changeLogFileName}</Text>
                    <Tooltip title="Changelog document information">
                      <InfoCircleOutlined style={{fontSize: '14px', color: '#666'}} />
                    </Tooltip>
                  </div>
                  <Text style={{fontSize: '14px', color: '#262626', fontWeight: 500}}>
                    {model.metadata?.changelog_file
                      ? files?.find((f) => f.uuid === model.metadata?.changelog_file)?.name || '-'
                      : '-'}
                  </Text>
                </div>
              </div>
            </>,
          )}

          {/* Stage */}
          {renderSection(
            'Stage',
            <>
              <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px'}}>
                <div>
                  <Text style={{fontSize: '14px', color: '#666', display: 'block', marginBottom: '8px'}}>
                    {fields.model_development_plan}
                  </Text>
                  <Text style={{fontSize: '14px', color: '#262626', fontWeight: 500}}>
                    {model.metadata?.model_development_plan ? (
                      <a href={model.metadata.model_development_plan} target="_blank" rel="noopener noreferrer">
                        {model.metadata.model_development_plan}
                      </a>
                    ) : (
                      '-'
                    )}
                  </Text>
                </div>
                <div>
                  <Text style={{fontSize: '14px', color: '#666', display: 'block', marginBottom: '8px'}}>
                    {fields.stage_of_model_development} <span style={{color: '#ff4d4f'}}>*</span>
                  </Text>
                  <Text style={{fontSize: '14px', color: '#262626', fontWeight: 500}}>
                    {model.metadata?.stage_of_model_development || '-'}
                  </Text>
                </div>
              </div>
            </>,
          )}

          {/* Link My Model Entry */}
          {renderSection(
            'Link My Model Entry',
            <>
              {model.metadata?.tree_parent_id && (
                <Paragraph style={{fontSize: '13px', color: '#595959', marginBottom: '8px'}}>
                  Based on your metadata, the following possible parents were found.
                </Paragraph>
              )}
              <Text style={{fontSize: '13px', color: '#595959', display: 'block', marginBottom: '8px'}}>
                Link the parent entry. All internal users will see this link.
              </Text>
              <Text style={{fontSize: '13px', color: '#595959', display: 'block', marginBottom: '12px'}}>
                Only people you grant permission will see the files for your entry.
              </Text>
              <Text style={{fontSize: '14px', color: '#006A63', display: 'block', fontWeight: 500}}>
                {linkMyModelEntryValue}
              </Text>
            </>,
          )}
        </div>

        {/* Permissions Section */}
        <div style={{marginBottom: '40px'}}>
          <Title level={2} style={{fontSize: '20px', fontWeight: 500, marginBottom: '16px', color: '#262626'}}>
            Permissions
          </Title>

          <div
            style={{
              background: '#fafafa',
              border: '2px dashed #d9d9d9',
              borderRadius: '8px',
              padding: '24px',
            }}
          >
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: '40px', rowGap: '0'}}>
              <Title level={4} style={{fontSize: '16px', fontWeight: 500, marginBottom: '16px', color: '#006A63'}}>
                Model Roles
              </Title>
              <Title level={4} style={{fontSize: '16px', fontWeight: 500, marginBottom: '16px', color: '#006A63'}}>
                Access Permissions
              </Title>

              {renderPermissionCell(
                'Value & Implementation Lead',
                permissions.vestLead ? (
                  renderUsersOrDash([permissions.vestLead])
                ) : (
                  <Text style={{fontSize: '14px', color: '#8c8c8c'}}>-</Text>
                ),
                true,
              )}
              {renderPermissionCell('Viewers', renderUsersOrDash(permissions.viewers))}

              {renderPermissionCell(
                'BARDS HEDS Lead',
                modelSteward ? (
                  renderUsersOrDash([modelSteward])
                ) : (
                  <Text style={{fontSize: '14px', color: '#8c8c8c'}}>-</Text>
                ),
                true,
              )}
              {webModel
                ? renderPermissionCell('Display Viewers', renderUsersOrDash(permissions.displayViewers))
                : renderPermissionCell('', <span />)}

              {renderPermissionCell('Editors', renderUsersOrDash(permissions.owners))}
              {webModel
                ? renderPermissionCell('Calibration Modelers', renderUsersOrDash(permissions.calibrationModelers))
                : renderPermissionCell('', <span />)}

              {renderPermissionCell('', <span />)}
              {webModel
                ? renderPermissionCell('Scenario Modelers', renderUsersOrDash(permissions.scenarioModelers))
                : renderPermissionCell('', <span />)}
            </div>
          </div>
        </div>

        {/* HEMT-8938: AI Summary Disclaimer Section */}
        <div style={{marginBottom: '40px'}}>
          <Title level={2} style={{fontSize: '20px', fontWeight: 500, marginBottom: '16px', color: '#262626'}}>
            AI-Generated Summary for Your Model Entry
          </Title>

          <div
            style={{
              background: '#fafafa',
              border: '1px solid #e8e8e8',
              borderRadius: '8px',
              padding: '20px 24px',
            }}
          >
            <Text style={{fontSize: '13px', color: '#595959', display: 'block', marginBottom: '12px'}}>
              An AI-generated summary will be provided for your model entry based on the available Model Development
              Plan and Technical Report. Do you want to proceed or opt out?
            </Text>

            {onAiSummaryOptedOutChange && (
              <Flex align="center" gap={8}>
                <Text style={{fontSize: '13px', color: '#595959'}}>Opt out</Text>
                <Switch
                  checked={!aiSummaryOptedOut}
                  onChange={(checked) => onAiSummaryOptedOutChange(!checked)}
                  style={{marginBottom: 0}}
                />
                <Text style={{fontSize: '13px', color: '#595959'}}>Proceed</Text>
              </Flex>
            )}
          </div>
        </div>

        {/* Footer Buttons */}
        {buttons && buttons({onBack, nextHtmlType: 'button'})}
      </div>
    </div>
  );
};

export default StepReviewRedesigned;
